import { ConfidenceTier, RedForceActivityProvenanceSource, StudyMissionStatus } from '@@types/api.types';
import {
  getLegalMissionStatusTransitions,
  shouldRequireMissionStatusReason,
} from '@azymmetric/shared/meia/mission-status.constants';
import type { BadgeProps } from '@components/ui/badge';
import {
  getConfidenceTierLabel,
  getStudyMissionStatusLabel,
  getRedForceActivityProvenanceSourceLabel as getStudyRedForceActivityProvenanceSourceLabel,
} from '@utils/meia-enum.util';

export { MISSION_STATUS_REASON_MIN_LENGTH } from '@azymmetric/shared/meia/mission-status.constants';

type MissionCloseGateAcknowledgementParams = {
  redActivityBindingCount: number;
  targetStatus: StudyMissionStatus;
};

const confidenceTierBadgeVariantByTier: Record<ConfidenceTier, BadgeProps['variant']> = {
  [ConfidenceTier.A]: 'success',
  [ConfidenceTier.B]: 'active',
  [ConfidenceTier.C]: 'warning',
  [ConfidenceTier.D]: 'pending',
  [ConfidenceTier.F]: 'destructive',
};

const studyMissionStatusBadgeVariantByStatus: Record<StudyMissionStatus, BadgeProps['variant']> = {
  [StudyMissionStatus.ACTIVE]: 'active',
  [StudyMissionStatus.CLOSED]: 'status-archived',
  [StudyMissionStatus.DRAFT]: 'status-draft',
  [StudyMissionStatus.UNDER_REVIEW]: 'warning',
};

export const getConfidenceTierBadgeVariant = (tier: ConfidenceTier): BadgeProps['variant'] =>
  confidenceTierBadgeVariantByTier[tier];

export { getConfidenceTierLabel, getStudyMissionStatusLabel };

export const getNextStudyMissionStatuses = (status: StudyMissionStatus): StudyMissionStatus[] =>
  getLegalMissionStatusTransitions(status);

export const getRedForceActivityProvenanceSourceLabel = (source: RedForceActivityProvenanceSource): string =>
  getStudyRedForceActivityProvenanceSourceLabel(source);

export const getStudyMissionStatusBadgeVariant = (status: StudyMissionStatus): BadgeProps['variant'] =>
  studyMissionStatusBadgeVariantByStatus[status];

export const shouldRequireMissionCloseGateAcknowledgement = (
  params: MissionCloseGateAcknowledgementParams,
): boolean => {
  const { redActivityBindingCount, targetStatus } = params;

  return redActivityBindingCount === 0 && targetStatus === StudyMissionStatus.CLOSED;
};

export const shouldRequireMissionReopenReason = (currentStatus: StudyMissionStatus): boolean =>
  shouldRequireMissionStatusReason(currentStatus);

const redForceActivityTierLabelByTier: Record<number, string> = {
  1: 'Strategic',
  2: 'Operational',
  3: 'Tactical',
};

export const getRedForceActivityTierLabel = (tier: number): string =>
  redForceActivityTierLabelByTier[tier] ?? `Tier ${tier}`;

const confidenceTierDescriptionByTier: Record<ConfidenceTier, string> = {
  [ConfidenceTier.A]: 'Confidence A — validated by authoritative intelligence; highest reliability.',
  [ConfidenceTier.B]: 'Confidence B — corroborated reporting; high reliability.',
  [ConfidenceTier.C]: 'Confidence C — single-source or expert judgment; moderate reliability.',
  [ConfidenceTier.D]: 'Confidence D — uncorroborated reporting; low reliability.',
  [ConfidenceTier.F]: 'Confidence F — unvetted or migrated content; not yet reviewed.',
};

export const getConfidenceTierDescription = (tier: ConfidenceTier): string => confidenceTierDescriptionByTier[tier];

const provenanceSourceDescriptionBySource: Record<RedForceActivityProvenanceSource, string> = {
  [RedForceActivityProvenanceSource.Admin]: 'Curated by an Azymmetric administrator.',
  [RedForceActivityProvenanceSource.IntelPkg]: 'Sourced from a distributed intelligence package.',
  [RedForceActivityProvenanceSource.JLLIS]: 'Sourced from the Joint Lessons Learned Information System.',
  [RedForceActivityProvenanceSource.Sme]: 'Captured from subject-matter-expert input.',
  [RedForceActivityProvenanceSource.User]: 'Entered by a user or migrated from legacy data; pending review.',
};

export const getRedForceActivityProvenanceSourceDescription = (source: RedForceActivityProvenanceSource): string =>
  provenanceSourceDescriptionBySource[source];
